import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { LoadingState } from "../components/common/StateViews";
import { AppShell } from "../components/layout/AppShell";
import { useSettings } from "../components/settings/SettingsProvider";
import { t } from "../lib/i18n";
import { useRouteSeo } from "../lib/seo";

const HomePage = lazy(() => import("../pages/HomePage"));
const AthkarPage = lazy(() => import("../pages/AthkarPage"));
const QuranPage = lazy(() => import("../pages/QuranPage"));
const DonatePage = lazy(() => import("../pages/DonatePage"));
const FavoritesPage = lazy(() => import("../pages/FavoritesPage"));
const PrayerTimesPage = lazy(() => import("../pages/PrayerTimesPage"));
const QiblaPage = lazy(() => import("../pages/QiblaPage"));
const MoonPage = lazy(() => import("../pages/MoonPage"));
const ManasikPage = lazy(() => import("../pages/ManasikPage"));
const DuasPage = lazy(() => import("../pages/DuasPage"));
const TasbihPage = lazy(() => import("../pages/TasbihPage"));
const AlkhaliliPage = lazy(() => import("../pages/AlkhaliliPage"));
const AboutPage = lazy(() => import("../pages/AboutPage"));
const ContactPage = lazy(() => import("../pages/ContactPage"));
const NotFoundPage = lazy(() => import("../pages/NotFoundPage"));

function currentRoute(): string {
  const raw = window.location.hash.replace(/^#/, "") || "/";
  const path = raw.split("?")[0] ?? "/";
  const hashPath = path.startsWith("/") ? path : `/${path}`;
  if (hashPath !== "/") return normalizeLegacyRoute(hashPath);
  return normalizeLegacyRoute(window.location.pathname || "/");
}

function normalizeLegacyRoute(path: string): string {
  const cleaned = path.replace(/\/+$/, "") || "/";
  const aliases: Record<string, string> = {
    "/index.html": "/",
    "/pages/athkar": "/athkar",
    "/pages/athkar.html": "/athkar",
    "/pages/favorites": "/favorites",
    "/pages/favorites.html": "/favorites",
    "/pages/qibla": "/qibla",
    "/pages/qibla.html": "/qibla",
    "/pages/moon": "/moon",
    "/pages/moon.html": "/moon",
    "/pages/alkhalili": "/alkhalili",
    "/pages/alkhalili.html": "/alkhalili",
    "/pages/about": "/about",
    "/pages/about.html": "/about"
  };
  return aliases[cleaned] ?? cleaned;
}

export function Router() {
  const [route, setRoute] = useState(currentRoute);
  const { settings } = useSettings();

  useRouteSeo(route, settings.locale);

  useEffect(() => {
    const onHash = () => setRoute(currentRoute());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const page = useMemo(() => {
    const props = { locale: settings.locale };
    switch (route) {
      case "/":
        return <HomePage {...props} />;
      case "/athkar":
        return <AthkarPage {...props} />;
      case "/favorites":
        return <FavoritesPage {...props} />;
      case "/quran":
        return <QuranPage {...props} />;
      case "/donate":
        return <DonatePage {...props} />;
      case "/prayer":
        return <PrayerTimesPage {...props} />;
      case "/qibla":
        return <QiblaPage {...props} />;
      case "/moon":
        return <MoonPage {...props} />;
      case "/manasik":
        return <ManasikPage {...props} />;
      case "/duas":
        return <DuasPage {...props} />;
      case "/tasbih":
        return <TasbihPage {...props} />;
      case "/alkhalili":
        return <AlkhaliliPage {...props} />;
      case "/about":
        return <AboutPage {...props} />;
      case "/contact":
        return <ContactPage {...props} />;
      default:
        return <NotFoundPage {...props} />;
    }
  }, [route, settings.locale]);

  return (
    <AppShell route={route}>
      <Suspense fallback={<LoadingState label={t(settings.locale, "loading")} />}>
        {page}
      </Suspense>
    </AppShell>
  );
}
